import { Journey } from '@audibene/ta.utils-opportunity'
import { addBusinessDays, addDays, startOfDay, startOfMonth } from 'date-fns'
import { zonedTimeToUtc } from 'date-fns-tz'
import { useMemo } from 'react'
import { useWatch } from 'react-hook-form'
import { useParams } from 'react-router'
import { AppointmentType, AvailabilityDefaultParameters, RouteParameters, ResourceType } from '../../../../../constants'
import { useAcfuProgressiveDisclosure, useAppointmentTypes, useFeatureFlags, useNow, useShop } from '../../../../../hooks'
import { AppointmentFormData, Resource, ResourceId } from '../../../../../types'
import { prop } from '../../../../../util'
import { useIsCabinOnlyConfiguration } from '../useIsCabinOnlyConfiguration'
import { useResourcesByResourceConfigurations } from '../useResourcesByResourceConfigurations'

interface AvailabilityParameters {
  appointment_type: AppointmentType
  date_end: Date
  date_start: Date
  durationInMinutes: number
  isCabinOnlyConfiguration: boolean
  journey: Journey
  resources: Resource[]
  resourceIds: ResourceId[]
  timeZone: string
}

type UseHookContext = AvailabilityParameters | undefined

function clampToStartOfMonthUtc(date: Date): Date {
  const now = new Date()

  // Start of this month in UTC
  const startOfThisMonthUtc = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1, 0, 0, 0, 0))

  // If input date < start of month → return start of month
  if (date.getTime() < startOfThisMonthUtc.getTime()) {
    return startOfThisMonthUtc
  }

  // Otherwise keep the original date
  return date
}

const startDateToMonthDateRange = (startDate: Date, timeZone: string, windowInDays: number) => {
  const start = zonedTimeToUtc(startOfDay(startDate), timeZone)
  const end = addDays(start, windowInDays)

  return { end, start: clampToStartOfMonthUtc(start) }
}

const useAvailabilityParameters = (): UseHookContext => {
  const { shop } = useShop()
  const shopIds = shop ? [shop.id] : undefined

  const [shouldSkipTwoBusinessDaysFeatureFlag, availabilityWindowInDays] = useFeatureFlags([
    'SKIP_48_BUSINESS_HOURS_ON_NEW_HEARING_AID_EVALUATIONS',
    'SCHEDULING_AVAILABILITY_WINDOW_IN_DAYS',
  ])
  const fallbackWindowInDays =
    typeof availabilityWindowInDays === 'number' ? availabilityWindowInDays : AvailabilityDefaultParameters.MAX_AVAILABILITIES_DATE_RANGE
  const { windowInDays } = useAcfuProgressiveDisclosure(fallbackWindowInDays)

  const [optionalTimeZone, optionalDurationInMinutes, optionalJourney, optionalMonthDate, optionalAppointmentType] =
    useWatch<AppointmentFormData>({
      name: ['customer_timezone', 'duration_in_minutes', 'journey', 'month_date', 'type'],
    })

  const appointmentType = optionalAppointmentType as AppointmentType
  const durationInMinutes = optionalDurationInMinutes as number
  const monthDate = optionalMonthDate as Date
  const journey = optionalJourney as Journey
  const timeZone = optionalTimeZone as string

  const isCabinOnlyConfiguration = useIsCabinOnlyConfiguration()

  const { [RouteParameters.APPOINTMENT_ID]: appointmentId } = useParams()
  const isRescheduling = Boolean(appointmentId)

  const { data: now = new Date() } = useNow()
  const safeDate = addDays(now, -1)
  const twoBusinessDays = addBusinessDays(now, 2)
  const nearestPossibleDate =
    shouldSkipTwoBusinessDaysFeatureFlag && appointmentType === AppointmentType.HEARING_AID_EVALUATION && !isRescheduling
      ? twoBusinessDays
      : safeDate
  const startDate = monthDate.getMonth() === safeDate.getMonth() ? nearestPossibleDate : zonedTimeToUtc(startOfMonth(monthDate), timeZone)
  const { end: date_end, start: date_start } = useMemo(
    () => startDateToMonthDateRange(startDate, timeZone, windowInDays),
    [startDate, timeZone, windowInDays]
  )

  const { appointmentTypes } = useAppointmentTypes()
  const config = appointmentTypes?.[appointmentType]

  const {
    isError: hasResourcesErrored,
    isFetching: areResourcesFetching,
    resources: allResources,
  } = useResourcesByResourceConfigurations({
    journey,
    resourceConfigurations: config?.resource_configurations,
    shopIds,
  })

  // For CABIN_ONLY configuration, only include cabin resources for availability fetching
  // This is used because we are skipping the fitter/specialist resources for availability fetching
  // if we don't do this, we will get availability for the fitter/specialist resources, which will be incorrect
  const resources = useMemo(
    () => (isCabinOnlyConfiguration ? allResources?.filter((r) => r.type === ResourceType.CABIN) : allResources),
    [isCabinOnlyConfiguration, allResources]
  )

  const resourceIds = resources?.map(prop('id')) ?? ([] as ResourceId[])

  if (hasResourcesErrored || areResourcesFetching) {
    return undefined
  }

  return {
    appointment_type: appointmentType,
    date_end,
    date_start,
    durationInMinutes,
    isCabinOnlyConfiguration,
    journey,
    resources,
    resourceIds,
    timeZone,
  }
}

export { useAvailabilityParameters }
